import React from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ProtectedRoute = ({ children, adminOnly = false, dashboardOnly = false, zoneOnly = false }) => {
  const { isAuthenticated, isAdmin, hasZoneDashboardAccess, hasMainDashboardAccess, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fsf-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Vérification de l'authentification...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Rediriger vers la page de connexion avec l'URL de destination
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Les simples membres utilisent leur espace personnel (PWA)
  const isMember = user?.role?.toLowerCase() === 'membre';

  if (isMember && (dashboardOnly || zoneOnly)) {
    return <Navigate to="/member-dashboard" replace />;
  }

  if (adminOnly && !isAdmin) {
    // Rediriger vers l'accueil si l'utilisateur n'est pas admin
    return <Navigate to="/" replace />;
  }

  if (zoneOnly && !hasZoneDashboardAccess()) {
    // Rediriger automatiquement vers le dashboard principal si l'utilisateur a accès
    if (hasMainDashboardAccess()) {
      return <Navigate to="/dashboard" replace />;
    }
    
    // Afficher un message d'erreur pour accès non autorisé au dashboard de zone
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès non autorisé - Zone Dashboard</h2>
          <p className="text-gray-600 mb-4">
            Vous n'avez pas les permissions nécessaires pour accéder au dashboard de zone.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-yellow-800">
              <strong>Votre rôle :</strong> {user?.role || 'Non défini'}
            </p>
            <p className="text-sm text-yellow-800 mt-1">
              <strong>Zone :</strong> {user?.zone || 'Non assigné'}
            </p>
            <p className="text-sm text-yellow-800 mt-1">
              <strong>Rôles autorisés :</strong> APZ, SGZ, TGZ, MCZ
            </p>
          </div>
          <div className="space-y-3">
            <Link 
              to="/" 
              className="block w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2 px-4 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300"
            >
              Retour à l'accueil
            </Link>
            <p className="text-sm text-gray-500">
              Contactez l'administrateur pour modifier vos permissions.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (dashboardOnly && !hasMainDashboardAccess()) {
    // Rediriger automatiquement vers le dashboard zone si l'utilisateur a accès
    if (hasZoneDashboardAccess()) {
      return <Navigate to="/zone-dashboard" replace />;
    }
    
    // Afficher un message d'erreur pour accès non autorisé au dashboard principal
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès non autorisé - Dashboard Principal</h2>
          <p className="text-gray-600 mb-4">
            Vous n'avez pas les permissions nécessaires pour accéder au dashboard administratif.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-yellow-800">
              <strong>Votre rôle :</strong> {user?.role || 'Non défini'}
            </p>
            <p className="text-sm text-yellow-800 mt-1">
              <strong>Rôles autorisés :</strong> Admin, APD, SGD, TGD, MCD
            </p>
          </div>
          <div className="space-y-3">
            <Link 
              to="/" 
              className="block w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2 px-4 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300"
            >
              Retour à l'accueil
            </Link>
            <p className="text-sm text-gray-500">
              Contactez l'administrateur pour modifier vos permissions.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;